import { ArrowRightIcon } from '@heroicons/react/16/solid'
import { Link } from 'react-router'
import type {
  ToolInvocation,
  ToolRendererOptions,
} from 'tailwind-v4/blocks/agent-chat'
import { useKnowledgeBasePanel } from '@/providers/knowledge-base-panel.context'

/**
 * Trunk's `search-knowledge` tool output (knowledge-base-agent-ard.md D1) —
 * `path` is already resolved (org/venue templated server-side) by the time
 * it reaches here, so this card just renders it as a real link. Never build
 * or guess a URL client-side from partial data.
 */
interface SearchKnowledgeResult {
  section: string
  content: string
  similarity_score: number
  path: string | null
}

interface SearchKnowledgeOutput {
  results: SearchKnowledgeResult[]
  total_found: number
}

/**
 * A follow-up prompt queued for auto-send once the panel lands on the
 * destination page (KnowledgeBasePanelProvider's `pendingFollowUp`,
 * consumed by side-panel.tsx). Without this, clicking a link just
 * navigates — the transcript still shows only the ORIGINAL question, even
 * though the destination may be several links deep from what was actually
 * asked, and may need a venue selected before it means anything. Naming
 * the section explicitly (rather than "tell me more about this") gives the
 * model an unambiguous anchor even though the chunk itself already covered
 * this ground once — the point is the NEW context (now actually on the
 * page, `appContext.pathname` matching) that the "NAVIGATION AWARENESS"
 * block in knowledge-base.ts's instructions can use to give the "you're
 * here, do X" follow-through instead of repeating the how-to.
 */
function buildFollowUpPrompt(section: string): string {
  return `I've just gone to "${section}" from your last answer. Now that I'm here, what should I do?`
}

/**
 * One "Go to page →" link per matched chunk that has a `path`. Chunks
 * without one (purely conceptual answers) are skipped silently — the
 * model's prose already covers them, there's nothing to link to.
 *
 * Clicking one navigates to the plain destination path AND calls
 * `openPanel(threadId, followUp)` on the shared panel context
 * (panel-context.tsx) — that context, not the URL, is what keeps the
 * docked side panel open and on the same thread across the navigation, and
 * `followUp` is what makes it actually SAY something once it lands rather
 * than sitting on the old exchange. An earlier version encoded
 * `?knowledge_base=true&thread_id=…` into the href instead; that broke the
 * moment the user did anything ELSE that navigates (venue switcher,
 * sidebar, breadcrumbs) without knowing to carry those params forward.
 */
export function SearchKnowledgeLinksCard({
  output,
  knowledgeBaseThreadId,
}: {
  output: SearchKnowledgeOutput
  knowledgeBaseThreadId?: string
}) {
  const { openPanel } = useKnowledgeBasePanel()

  // A path can come back with a literal `{serial}` still in it when no
  // venue was known server-side (search-knowledge.ts's resolvePath leaves
  // the placeholder rather than guessing) — never render that as a
  // clickable link, the model is instructed to ask which venue instead.
  const links = (output.results ?? []).filter(
    (r): r is SearchKnowledgeResult & { path: string } =>
      Boolean(r.path) && !r.path!.includes('{')
  )

  if (links.length === 0) return null

  return (
    <div className="flex flex-col gap-1.5 my-1">
      {links.map((r) => (
        <Link
          key={r.path}
          to={r.path}
          onClick={() =>
            openPanel(
              knowledgeBaseThreadId ?? null,
              buildFollowUpPrompt(r.section)
            )
          }
          // `--brand-warm` (#ff6b4a) is the real Stampede orange used
          // elsewhere in this app (nav-v4's sidebar-shell-core.tsx, the
          // day-planner's move-confirm-dialog) — the `stampede-*` Tailwind
          // scale is actually brand PINK (#ff3a9b), not orange; don't reuse
          // it here even though the name suggests otherwise.
          className="inline-flex items-center gap-1.5 self-start rounded-lg border border-[var(--brand-warm,#ff6b4a)]/30 bg-[var(--brand-warm,#ff6b4a)]/[0.06] px-3 py-1.5 text-sm font-medium text-[var(--brand-warm,#ff6b4a)] hover:bg-[var(--brand-warm,#ff6b4a)]/[0.12] transition-colors"
        >
          {r.section}
          <ArrowRightIcon className="size-3.5" />
        </Link>
      ))}
    </div>
  )
}

export function searchKnowledgeToolRenderer(
  toolCall: ToolInvocation,
  options?: ToolRendererOptions
) {
  const output = toolCall.result as SearchKnowledgeOutput | undefined
  if (!output) return null
  return (
    <SearchKnowledgeLinksCard
      output={output}
      knowledgeBaseThreadId={options?.knowledgeBaseThreadId}
    />
  )
}
